import type { ReactNode } from 'react';
import { LockKeyhole, MessageCircle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { FeatureKey } from '@shared/config';
import { assistedOfferWhatsAppUrl } from '@shared/config';
import { Badge, Card, CardContent, buttonVariants } from '@/components/ui';
import { FeatureError, FeatureLoading } from '@/features/_shared/FeatureUI';
import { useFeatureCopy } from '@/features/_shared/copy';
import { useFeatureEntitlement } from '@/lib/feature-entitlements';
import { routes } from '@/lib/routes';

export function PurchasedFeatureRoute({
  feature,
  children,
}: {
  feature: FeatureKey;
  children: ReactNode;
}) {
  const { copy } = useFeatureCopy('purchaseGate');
  const entitlement = useFeatureEntitlement(feature);
  if (entitlement.isLoading) {
    return (
      <FeatureLoading
        label={copy('loading', 'Checking purchaser access', 'بنتأكد من صلاحية الشراء')}
      />
    );
  }
  if (entitlement.isUnavailable) {
    return (
      <FeatureError
        title={copy('unavailable', 'Access check is unavailable', 'تعذّر التحقق من الصلاحية')}
        description={copy(
          'unavailableBody',
          'Please refresh the page. This feature stays locked until access can be verified.',
          'حدّث الصفحة من فضلك. الأداة هتفضل مقفولة لحد ما نقدر نتأكد من الصلاحية.',
        )}
        retryLabel={copy('refresh', 'Refresh page', 'حدّث الصفحة')}
        onRetry={() => window.location.reload()}
      />
    );
  }
  if (entitlement.decision.allowed) return children;

  return (
    <div className="mx-auto max-w-3xl py-6 sm:py-12">
      <Card className="overflow-hidden border-blue-200 shadow-xl shadow-blue-950/10">
        <CardContent className="p-0">
          <div className="bg-gradient-to-br from-slate-950 via-blue-950 to-blue-800 p-7 text-white sm:p-10">
            <span className="grid size-14 place-items-center rounded-2xl bg-white/10 text-cyan-300">
              <LockKeyhole size={27} />
            </span>
            <Badge className="mt-5 bg-white/10 text-cyan-100">
              <Sparkles size={14} /> {copy('purchaserOnly', 'Purchaser feature', 'ميزة للمشترين')}
            </Badge>
            <h1 className="mt-4 text-3xl font-bold text-white">
              {copy('title', 'Unlock your complete career toolkit', 'افتح كل أدوات تجهيزك للشغل')}
            </h1>
            <p className="mt-3 max-w-2xl leading-7 text-blue-100">
              {copy(
                'body',
                'Your free account still includes the career form, profile, ATS CV builder and PDF download. Purchase once to unlock CV checking, LinkedIn analysis, job matching, interviews and AI improvements.',
                'حسابك المجاني فيه الاستمارة والملف المهني وبناء سيرة ATS وتحميل PDF. اشترِ مرة واحدة علشان تفتح فحص السيرة وتحليل LinkedIn ومطابقة الوظائف والمقابلات وتحسينات الذكاء الاصطناعي.',
              )}
            </p>
            <div className="mt-7 flex flex-wrap items-end gap-3">
              <span className="text-lg text-blue-200 line-through">200 EGP</span>
              <strong className="text-4xl text-white">50 EGP</strong>
              <span className="pb-1 text-sm text-cyan-200">
                {copy('limited', 'limited-time offer', 'عرض لفترة محدودة')}
              </span>
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                className={buttonVariants({
                  size: 'lg',
                  className: 'bg-emerald-500 text-white hover:bg-emerald-600',
                })}
                href={assistedOfferWhatsAppUrl()}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle size={18} />{' '}
                {copy('whatsapp', 'Claim offer on WhatsApp', 'احجز العرض على واتساب')}
              </a>
              <Link
                className={buttonVariants({
                  variant: 'outline',
                  size: 'lg',
                  className: 'border-white/30 bg-white/10 text-white hover:bg-white/20',
                })}
                to={routes.pricing}
              >
                {copy('details', 'View offer details', 'شوف تفاصيل العرض')}
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
